import React, { useState, useEffect } from 'react';
import { 
  CheckSquare, Square, Edit2, Trash2, X, Save, 
  ChevronDown, Flag, User, Calendar 
} from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';

// Status options for bulk edit
const STATUSES = [
  'Not Started', 'In Progress', 'In Review', 'Blocked', 
  'Behind', 'Due Date at Risk', 'Completed'
];

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

/**
 * BulkEditToolbar - Shows when items are selected for bulk operations
 */
export const BulkEditToolbar = ({ 
  selectedIds = [], 
  itemType = 'work_item',
  onBulkUpdate,
  onBulkDelete,
  onClearSelection,
  sprints = [],
  releases = []
}) => {
  const { canEdit } = useTenant();
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showOwnerInput, setShowOwnerInput] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [ownerValue, setOwnerValue] = useState('');
  const [updating, setUpdating] = useState(false);

  if (selectedIds.length === 0 || !canEdit()) return null;

  const handleBulkUpdate = async (field, value) => {
    setUpdating(true);
    try {
      await api.bulkUpdateWorkItems({
        item_ids: selectedIds,
        item_type: itemType,
        updates: { [field]: value }
      });
      if (onBulkUpdate) onBulkUpdate(field, value);
    } catch (error) {
      console.error('Bulk update failed:', error);
    } finally {
      setUpdating(false);
      setShowStatusDropdown(false);
      setShowOwnerInput(false);
      setShowPriorityDropdown(false);
    }
  };

  return (
    <div 
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-900 text-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-4"
      data-testid="bulk-edit-toolbar"
    >
      <div className="flex items-center gap-2 pr-4 border-r border-gray-700">
        <CheckSquare className="w-5 h-5 text-blue-400" />
        <span className="font-medium">{selectedIds.length} selected</span>
      </div>

      {/* Status Change */}
      <div className="relative">
        <button
          onClick={() => setShowStatusDropdown(!showStatusDropdown)}
          className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center gap-2 text-sm"
          disabled={updating}
        >
          <Flag className="w-4 h-4" />
          Status
          <ChevronDown className="w-3 h-3" />
        </button>
        {showStatusDropdown && (
          <div className="absolute bottom-full mb-2 left-0 bg-white text-[var(--clr-text-primary)] rounded-lg shadow-xl border py-1 min-w-40">
            {STATUSES.map(status => (
              <button
                key={status}
                onClick={() => handleBulkUpdate('status', status)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--clr-surface-hover)]"
              >
                {status}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Priority Change */}
      <div className="relative">
        <button
          onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
          className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center gap-2 text-sm"
          disabled={updating}
        >
          Priority
          <ChevronDown className="w-3 h-3" />
        </button>
        {showPriorityDropdown && (
          <div className="absolute bottom-full mb-2 left-0 bg-white text-[var(--clr-text-primary)] rounded-lg shadow-xl border py-1 min-w-32">
            {PRIORITIES.map(priority => (
              <button
                key={priority}
                onClick={() => handleBulkUpdate('priority', priority)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--clr-surface-hover)]"
              >
                {priority}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Owner Change */}
      <div className="relative">
        <button
          onClick={() => setShowOwnerInput(!showOwnerInput)}
          className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center gap-2 text-sm"
          disabled={updating}
        >
          <User className="w-4 h-4" />
          Owner
        </button>
        {showOwnerInput && (
          <div className="absolute bottom-full mb-2 left-0 bg-white text-[var(--clr-text-primary)] rounded-lg shadow-xl border p-3 min-w-60">
            <input
              type="text"
              placeholder="Enter owner name"
              value={ownerValue}
              onChange={(e) => setOwnerValue(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm mb-2"
              autoFocus
            />
            <button
              onClick={() => handleBulkUpdate('owner', ownerValue)}
              className="w-full px-3 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg text-sm hover:bg-[var(--clr-brand-primary-hover)]"
              disabled={!ownerValue.trim()}
            >
              Apply to {selectedIds.length} items
            </button>
          </div>
        )}
      </div>

      {/* Sprint Assignment (if available) */}
      {sprints.length > 0 && (
        <div className="relative">
          <select
            onChange={(e) => e.target.value && handleBulkUpdate('sprint_id', e.target.value)}
            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm border-none appearance-none cursor-pointer"
            disabled={updating}
          >
            <option value="">Assign Sprint...</option>
            {sprints.map(sprint => (
              <option key={sprint.id} value={sprint.id}>{sprint.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="border-l border-gray-700 pl-4">
        <button
          onClick={onClearSelection}
          className="px-3 py-1.5 hover:bg-gray-700 rounded-lg flex items-center gap-2 text-sm"
        >
          <X className="w-4 h-4" />
          Clear
        </button>
      </div>

      {updating && (
        <div className="absolute inset-0 bg-gray-900/80 rounded-xl flex items-center justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
        </div>
      )}
    </div>
  );
};

/**
 * SelectableCheckbox - Checkbox for selecting items in lists
 */
export const SelectableCheckbox = ({ 
  checked, 
  onChange, 
  disabled = false,
  className = ''
}) => {
  const { canEdit } = useTenant();

  if (!canEdit()) return null;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      disabled={disabled}
      className={`p-1 rounded hover:bg-[var(--clr-surface-hover)] ${className}`}
      data-testid="selectable-checkbox"
    >
      {checked ? (
        <CheckSquare className="w-5 h-5 text-[var(--clr-brand-primary)]" />
      ) : (
        <Square className="w-5 h-5 text-[var(--clr-text-muted)]" />
      )}
    </button>
  );
};

/**
 * SelectAllCheckbox - Header checkbox for selecting all visible items
 */
export const SelectAllCheckbox = ({ 
  allSelected, 
  someSelected,
  onSelectAll,
  disabled = false
}) => {
  const { canEdit } = useTenant();

  if (!canEdit()) return null;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onSelectAll();
      }}
      disabled={disabled}
      className="p-1 rounded hover:bg-[var(--clr-surface-hover)]"
      data-testid="select-all-checkbox"
    >
      {allSelected ? (
        <CheckSquare className="w-5 h-5 text-[var(--clr-brand-primary)]" />
      ) : someSelected ? (
        <div className="w-5 h-5 border-2 border-[var(--clr-brand-primary)] rounded flex items-center justify-center">
          <div className="w-2 h-2 bg-[var(--clr-brand-primary)] rounded-sm"></div>
        </div>
      ) : (
        <Square className="w-5 h-5 text-[var(--clr-text-muted)]" />
      )}
    </button>
  );
};

/**
 * useSelectable - Hook for managing selection state
 */
export const useSelectable = (items = []) => {
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Clear selection when items change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [items.length]);

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(item => item.id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const isSelected = (id) => selectedIds.has(id);
  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < items.length;

  return {
    selectedIds: Array.from(selectedIds),
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
    allSelected,
    someSelected,
    selectionCount: selectedIds.size
  };
};

export default BulkEditToolbar;
